import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { capturePageview, setSessionRecording } from "../analytics";

/**
 * Capture one pageview per route change, for a router that never reloads the document.
 *
 * Mounted inside BrowserRouter (it needs useLocation), which is why this is a hook in a
 * component rather than a call in main.tsx.
 *
 * Paths are sent as-is EXCEPT for their variable segments, which are collapsed to a
 * label. Two separate reasons, and the second is the serious one:
 *
 *  1. One distinct "page" per view/collection/screen makes the pageview report useless —
 *     you want to know that the editor was opened, not which of 400 views it held.
 *  2. Some of those segments are CREDENTIALS, not just identifiers. `/claim/<code>`
 *     carries a live pairing code that mints a device for whoever redeems it, and
 *     `/control/<token>` is a control-handoff token. Sending either to a third party
 *     would be handing over the credential itself.
 *
 * MUST be kept in step with the routes in App.tsx and ControlApp.tsx. A new
 * `/thing/:id` route added without a line here silently starts emitting ids —
 * tools/checks/analytics-scrub.mts asserts the credential-bearing ones are covered.
 *
 * `/claim/:code` OUTLIVES ITS ROUTE, deliberately. The route was deleted with the screen
 * QR that produced it, but a bookmark, a browser history entry or a link somebody sent
 * still resolves here: the SPA serves index.html, this hook fires a pageview for the
 * current location, and only THEN does routing decide what to render (since 2026-09-26
 * the ControlApp splat shows Not Found rather than redirecting to `/`). Drop this line and
 * that dead link ships a live pairing code to a third party on its way to a 404. A scrub
 * for a URL nobody can generate any more is worth about nothing to keep and quite a lot to
 * have removed.
 */
const ID_SEGMENTS: Array<[RegExp, string]> = [
  [/^\/view\/d\/[^/]+$/, "/view/d/:id"],
  [/^\/play\/collection\/[^/]+$/, "/play/collection/:id"],
  [/^\/views\/[^/]+$/, "/views/:id"],
  [/^\/claim\/[^/]+$/, "/claim/:code"],
  [/^\/control\/[^/]+$/, "/control/:token"],
  [/^\/browse\/[^/]+$/, "/browse/:id"],
  [/^\/feed\/[^/]+$/, "/feed/:id"],
  // A room's id is its invite link.
  [/^\/room\/[^/]+$/, "/room/:id"],
  [/^\/collections\/[^/]+$/, "/collections/:id"],
  // A username is an identifier (and a person): the pageview says the public profile
  // was visited, never whose.
  [/^\/u\/[^/]+$/, "/u/:username"],
  // An order number is somebody's purchase.
  [/^\/shop\/orders\/[^/]+$/, "/shop/orders/:id"],
  // Which section was opened rides on shop_category_viewed, so the pageview can be a pattern.
  [/^\/shop\/category\/[^/]+$/, "/shop/category/:category"],
  // Not a route of ours at all — a media url opened directly in a tab, which the server
  // falls through to index.html for. Seen in production: two pageviews with a clip key
  // in the path. A file is a clip, and the key names one, so it is collapsed like an id.
  [/^\/media\/[^/]+$/, "/media/:id"],
];

/**
 * Segments that look like an id but are fixed names, so collapsing them would throw away
 * a real destination. `/collections/faps` is the likes list — a product surface every
 * account has, and what `/library` redirects to — not somebody's collection id.
 */
const NOT_IDS = new Set(["/collections/faps"]);

/**
 * Routes that are NEVER session-recorded. The wall and pending-screen ADDRESSES (`/view`,
 * `/screen`, `/room`), because a replay of them is the viewing history; play/mint routes,
 * which land on the wall; the admin panel; and the credential-bearing routes, because a
 * recording carries the page url and `/claim/<code>` / `/control/<token>` ARE credentials.
 * Everything else — browse, collections, views, the remote, account flows — is the UX
 * worth replaying. Kept beside ID_SEGMENTS so a new route is weighed for both at once; the
 * scrub check asserts the wall and credential routes are here.
 *
 * The front door `/` IS recorded, deliberately (2026-10-21), although it renders the same
 * PendingScreen the wall does. What it shows that is a credential is kept out of the
 * replay at the element instead: the pairing code in the bar's chip and the code card
 * wear MASK (lib/replayMask), and the party QR, which encodes `/claim/<code>`, is a
 * `ph-no-capture` box (display/PartyBadge). tools/checks/replay-mask.mts pins both.
 */
const NO_RECORDING: RegExp[] = [
  /^\/view(\/|$)/,
  // A room is a screen (the wall plays) whose address is a private link.
  /^\/room(\/|$)/,
  /^\/screen(\/|$)/,
  /^\/play(\/|$)/,
  /^\/claim(\/|$)/,
  /^\/control(\/|$)/,
  /^\/admin(\/|$)/,
  // A buyer's address is on screen here, and what they bought.
  /^\/shop\/(orders|thanks)(\/|$)/,
];
function mayRecord(pathname: string): boolean {
  return !NO_RECORDING.some((re) => re.test(pathname));
}

function normalize(pathname: string): string {
  if (NOT_IDS.has(pathname)) return pathname;
  for (const [re, label] of ID_SEGMENTS) if (re.test(pathname)) return label;
  return pathname;
}

export function useRouteAnalytics(): void {
  const { pathname } = useLocation();
  // A route change that lands on the same normalized path — one screen id to another —
  // is not a new pageview, and firing one would count screens instead of navigations.
  const last = useRef<string | null>(null);

  useEffect(() => {
    // Recording follows the route on EVERY change (not just new pageviews): leaving the
    // wall for the remote must start it, and arriving on the wall must stop it, even when
    // the normalized pageview is unchanged.
    //
    // FIRST, and before the admin bail below. Both of these landed on separate branches
    // and the order is the whole merge: `/admin` is in NO_RECORDING, so returning early
    // for it would skip the one call that stops recording — an admin tab would keep
    // recording whatever it had been recording on the way in.
    setSessionRecording(mayRecord(pathname));
    // Admin is us, not usage. Its pageviews would sit in the same numbers as visitors',
    // so they are not sent at all. (Signed-in activity IS identified now, but the admin
    // panel has its own password and no account behind it — there is still no person on
    // an admin tab to filter by.) The other events an admin tab emits carry
    // `role: "admin"` (AdminApp registers it) and can be excluded by that.
    if (pathname.startsWith("/admin")) return;
    const path = normalize(pathname);
    if (last.current === path) return;
    last.current = path;
    capturePageview(path);
  }, [pathname]);
}
